"use client";

import { useState } from "react";
import { PillGroup, SelectField } from "@/components/tool-kit/primitives";
import { HeatCurvePanel } from "@/components/tools/chem-physical/heat-curve-panel";
import { HeatLatentPanel } from "@/components/tools/chem-physical/heat-latent-panel";
import { HeatMixPanel } from "@/components/tools/chem-physical/heat-mix-panel";
import { HeatPanel } from "@/components/tools/chem-physical/heat-panel";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";

/** English strings. A translated content file overrides these through `ui`. */
export const SPECIFIC_HEAT_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to calculate?",
  modeHeat: "Heat, mass or temperature change",
  modeMix: "Final temperature of a mixture",
  modeLatent: "Heat of a phase change",
  modeCurve: "Heating curve for water",
  sigFigs: "Significant figures in the answer",
  sigMatch: "Match my data",
  sigChoice:
    "one: {count} significant figure|other: {count} significant figures",
  solveLabel: "Solve for",
  results: "Results",
  working: "The working",
  answerSuffix: "{label} (answer)",
  substanceLabel: "Substance (fills in the value)",
  substanceCustom: "Enter my own value",
  substanceHint:
    "Specific heats are at 25 °C, from OpenStax Chemistry 2e Table 5.1 and College Physics 2e Table 14.1. Real materials vary with temperature and purity.",
  latentSubstanceHint:
    "Latent heats are at the normal melting or boiling point and 1 atm (OpenStax College Physics 2e, Table 14.2).",
  unitEnergy: "Energy unit",
  unitMass: "Mass unit",
  unitSpecificHeat: "Specific heat unit",
  unitTemperature: "Temperature unit",
  unitCalorimeter: "Calorimeter unit",
  unitLatentHeat: "Latent heat unit",
  termQ: "Heat (q)",
  termM: "Mass (m)",
  termC: "Specific heat (c)",
  termTi: "Initial temperature (T₁)",
  termTf: "Final temperature (T₂)",
  baseSpecificHeat: "J/(g·K)",
  deltaT: "Temperature change (ΔT)",
  direction: "Direction of heat flow",
  absorbed: "Heat absorbed",
  released: "Heat released",
  equationHeat: "q = m × c × ΔT, with ΔT = T₂ − T₁",
  rearrangedQ: "q = m × c × ΔT",
  rearrangedM: "m = q ÷ (c × ΔT)",
  rearrangedC: "c = q ÷ (m × ΔT)",
  rearrangedTi: "T₁ = T₂ − ΔT, where ΔT = q ÷ (m × c)",
  rearrangedTf: "T₂ = T₁ + ΔT, where ΔT = q ÷ (m × c)",
  convertLine: "{label}: {value} {unit} = {converted}",
  deltaLine: "ΔT = T₂ − T₁ = {tf} − {ti} = {delta}",
  deltaFromQ: "ΔT = q ÷ (m × c) = {q} ÷ ({m} × {c}) = {delta}",
  substitutedQ: "q = {m} × {c} × {delta} = {result}",
  substitutedM: "m = {q} ÷ ({c} × {delta}) = {result}",
  substitutedC: "c = {q} ÷ ({m} × {delta}) = {result}",
  substitutedTi: "T₁ = T₂ − ΔT = {tf} − {delta} = {result}",
  substitutedTf: "T₂ = T₁ + ΔT = {ti} + {delta} = {result}",
  answerLine: "{result} = {answer}",
  matchNote:
    "This specific heat is within 5% of {name} ({value} J/(g·°C)) in the table.",
  mixObject1: "Object 1 (the one that cools)",
  mixObject2: "Object 2 (for example, the water)",
  mixM1: "Mass of object 1 (m₁)",
  mixC1: "Specific heat of object 1 (c₁)",
  mixT1: "Initial temperature of object 1 (T₁)",
  mixM2: "Mass of object 2 (m₂)",
  mixC2: "Specific heat of object 2 (c₂)",
  mixT2: "Initial temperature of object 2 (T₂)",
  mixCalorimeter: "Calorimeter heat capacity (C, optional)",
  mixFinalT: "Final temperature (T_f)",
  mixSpecificHeat1: "Specific heat of object 1 (c₁)",
  mixQ1: "Heat change of object 1 (q₁)",
  mixQ2: "Heat change of object 2 (q₂)",
  mixQ2Cal: "Heat gained by object 2 and calorimeter",
  mixBalance:
    "Heat lost by object 1 = heat gained by object 2 and the calorimeter, so q₁ + q₂ + q_cal = 0.",
  mixEquation: "m₁c₁(T_f − T₁) + m₂c₂(T_f − T₂) + C(T_f − T₂) = 0",
  mixRearrangedTf: "T_f = [m₁c₁T₁ + (m₂c₂ + C)T₂] ÷ [m₁c₁ + m₂c₂ + C]",
  mixRearrangedC1: "c₁ = −(m₂c₂ + C)(T_f − T₂) ÷ [m₁(T_f − T₁)]",
  mixUnitsNote: "Units in the working: g, J/(g·K), J/K and K.",
  mixSubstitutedTf:
    "T_f = [{m1} × {c1} × {t1} + ({m2} × {c2} + {cal}) × {t2}] ÷ [{m1} × {c1} + {m2} × {c2} + {cal}] = {result} K",
  mixSubstitutedC1:
    "c₁ = −({m2} × {c2} + {cal}) × ({tf} − {t2}) ÷ [{m1} × ({tf} − {t1})] = {result} J/(g·K)",
  mixCheck: "Check: q₁ = {q1}, q₂ = {q2}, q_cal = {qcal}. They sum to zero.",
  processLabel: "Process",
  processMelt: "Melting",
  processFreeze: "Freezing",
  processBoil: "Boiling",
  processCondense: "Condensing",
  latentQ: "Heat (q)",
  latentL: "Latent heat (L)",
  transitionTemperature: "Normal transition temperature",
  latentEquation:
    "q = m × L (the temperature stays constant during the change)",
  latentRearrangedQ: "q = m × L",
  latentRearrangedM: "m = q ÷ L",
  latentRearrangedL: "L = q ÷ m",
  latentSubstitutedQ: "q = {m} g × {l} J/g = {result}",
  latentSubstitutedM: "m = {q} J ÷ {l} J/g = {result}",
  latentSubstitutedL: "L = {q} J ÷ {m} g = {result}",
  latentSignNote:
    "Freezing and condensing release heat, so the heat is negative: q = −m × L.",
  latentMagnitudeNote:
    "Enter the heat as a positive amount; the process you chose decides whether it is absorbed or released.",
  curveStartTemperature: "Starting temperature",
  curveEndTemperature: "Final temperature",
  curveStartPhase: "Starting phase",
  curveEndPhase: "Final phase",
  phaseIceTitle: "Ice (solid)",
  phaseWaterTitle: "Liquid water",
  phaseSteamTitle: "Steam (gas)",
  phaseIce: "ice",
  phaseWater: "liquid water",
  phaseSteam: "steam",
  curveTotal: "Total heat",
  curveSteps: "Steps",
  columnStep: "Step",
  columnCalculation: "Calculation",
  columnHeat: "Heat (kJ)",
  stepWarm: "Change the temperature of {phase} from {from} °C to {to} °C",
  stepMelt: "Melt ice at {temperature} °C",
  stepFreeze: "Freeze water at {temperature} °C",
  stepBoil: "Boil water at {temperature} °C",
  stepCondense: "Condense steam at {temperature} °C",
  calcWarm: "{m} g × {c} J/(g·°C) × {dt} °C",
  calcLatent: "{m} g × {l} J/g",
  curveNote:
    "Assumes 1 atm, so ice melts at 0 °C and water boils at 100 °C. Specific heats: ice 2.093, water 4.184, steam 1.864 J/(g·°C) (OpenStax Chemistry 2e, Table 5.1). Latent heats: 334 J/g (fusion) and 2256 J/g (vaporization) (OpenStax College Physics 2e, Table 14.2).",
  enterValues: "Enter the remaining values to see the answer.",
  errorUnreadable:
    "{label} is not a number. Use digits, such as 2.50 or 1.5e3.",
  errorNonPositive: "{label} must be greater than zero.",
  errorBelowZero:
    "{label} is at or below absolute zero (0 K, −273.15 °C, −459.67 °F), which is impossible.",
  errorZeroValue: "{label} must not be zero.",
  errorPhase:
    "{label} does not fit the phase you chose at 1 atm: ice is at or below 0 °C, steam at or above 100 °C, and liquid water is between them.",
  errorZeroChange:
    "The temperature does not change (ΔT = 0), so mass and specific heat cannot be found from q. Enter two different temperatures.",
  errorSignMismatch:
    "q and ΔT must have the same sign: heat added raises the temperature and heat removed lowers it. Check the sign of q.",
  errorResultBelowZero:
    "That heat would take the temperature below absolute zero, which is impossible. Check the sign and size of q.",
  errorOutsideRange:
    "The final temperature must lie between the two starting temperatures.",
  copy: "Copy answer",
  copied: "Copied!",
  copyText: "{label}: {value}",
  substances: {
    water: "Water",
    ice: "Ice",
    steam: "Steam (water vapor)",
    ethanol: "Ethanol",
    aluminum: "Aluminum",
    iron: "Iron / steel",
    copper: "Copper",
    lead: "Lead",
    gold: "Gold",
    silicon: "Silicon",
    silver: "Silver",
    mercury: "Mercury",
    benzene: "Benzene",
    glycerin: "Glycerin",
    glass: "Glass",
    air: "Air",
    nitrogen: "Nitrogen",
    oxygen: "Oxygen",
    carbonDioxide: "Carbon dioxide",
    helium: "Helium",
    argon: "Argon",
    ammonia: "Ammonia",
  },
} as const;

type Mode = "heat" | "mix" | "latent" | "curve";
const SIG_CHOICES = ["3", "4", "5", "6"] as const;

export default function SpecificHeatCalculatorWidget() {
  const strings = useToolUi(SPECIFIC_HEAT_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("heat");
  const [sigChoice, setSigChoice] = useState("match");
  const panels = {
    heat: <HeatPanel sigChoice={sigChoice} strings={strings} />,
    mix: <HeatMixPanel sigChoice={sigChoice} strings={strings} />,
    latent: <HeatLatentPanel sigChoice={sigChoice} strings={strings} />,
    curve: <HeatCurvePanel sigChoice={sigChoice} strings={strings} />,
  };
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="sh-mode"
        onChange={setMode}
        options={[
          { value: "heat", label: strings.modeHeat },
          { value: "mix", label: strings.modeMix },
          { value: "latent", label: strings.modeLatent },
          { value: "curve", label: strings.modeCurve },
        ]}
        value={mode}
      />
      <div className="max-w-xs">
        <SelectField
          id="sh-sig"
          label={strings.sigFigs}
          onChange={setSigChoice}
          options={[
            { value: "match", label: strings.sigMatch },
            ...SIG_CHOICES.map((value) => ({
              value,
              label: tPlural(strings.sigChoice, locale, Number(value), {
                count: fmt(Number(value)),
              }).trim(),
            })),
          ]}
          value={sigChoice}
        />
      </div>
      {panels[mode]}
    </>
  );
}
